/**
 * Taxes & Fees — the fee block of Premium Selection.
 *
 * Split out of the old combined "Fees & Premium" card (Andressa, approved
 * 4-ago): fees live here, the premium figures moved to `PremiumCard`. The
 * rationale in the design notes is that separating them lets the underwriter
 * test a selected premium against the loss-year figures sitting next to each
 * other, and leaves the interface less crowded.
 *
 * Rows: Tech/RM Fee and TRIA on white, then the InsCipher group — its
 * trigger row plus the three rates it authors (Surplus Lines Tax, Surplus
 * Lines Fee, Stamping Fee) — shaded as one block.
 *
 * Tech/RM Fee is a dollar input since backend PR #371. The backend derives it as
 * covered facilities × $5,000 until the underwriter types over it — not every
 * facility on a policy carries the Gemini service (a real pricing model had 32
 * SNFs and a $30,000 fee) — after which `tech_rm_fee_manually_set` is true and
 * the schedule no longer moves it. The backend stores a hand-typed fee as
 * whole facilities × $5,000, so the input only accepts multiples of $5,000
 * and never rounds on the user's behalf. 0 removes the fee (OR-1700).
 *
 * TRIA is a manual whole-percent input (Andressa, jun-17: editable, shown as
 * %); its $ contribution (Selected Premium × 5% × pct%, Henry OR-1513) folds
 * into Final Premium over in `PremiumCard`. The InsCipher trio differs on two
 * counts: their $ contribution defaults to Selected Premium × pct% alone
 * (Tech/RM Fee only enters the base when the risk's state says so — a
 * per-state rule all three fields share, OR-1440), and once InsCipher has
 * calculated, the $ shown is the amount it filed, not a recomputation
 * (backend PR #321).
 *
 * The "+Add Fee" header action (OR-1339) opens `AddFeeModal`, letting the
 * underwriter pick from the three predefined extra fees below; each added
 * fee then shows as its own row here, deletable in place via its trash
 * icon (no need to reopen the modal). Custom (free-text) fees (OR-1494) come
 * through the same modal: they're misc fees owned by the page (`miscFees`),
 * created and deleted via `/quote/misc-fees/`, and render as their own rows
 * right after the predefined ones.
 */
import { useEffect, useRef, useState } from "react";
import { Trash02 } from "@untitledui/icons";
import { Button, ButtonUtility } from "@tricura/atlas-ui";
import { MoneyInput } from "../../../../components/money-input";
import { PercentInput } from "../../../../components/percent-input";
import { FormGroup } from "../../../../components/form-group";
import { EXTRA_FEE_TYPES, isValidTechRmFee } from "../../../../domain/pricing";
import type { PremiumSelectionDerived, PremiumSelectionInputs } from "../../../../domain/pricing";
import type { MiscFee } from "../../../../types/misc-fee";
import { AddFeeModal } from "../components/add-fee-modal";
import { CardRows } from "../components/field-row";
import { InsCipherRow, type TaxCalcPhase } from "../components/inscipher-row";
import { useBlurFlash } from "../components/use-blur-flash";
import { clampMoneyToWire, clampPctToWire, displayMoneyValue } from "../premium-selection-types";
import type { InputKey } from "../use-premium-selection";

/** Taxes & Fees header glyph — Andressa's Figma export ("MVP 1 Updates
 *  07/28"). Stroke uses currentColor so FormGroup's brand color + size apply;
 *  the exported asset hardcodes #839FD1. */
function TaxesFeesIcon({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M4 7.8C4 6.11984 4 5.27976 4.32698 4.63803C4.6146 4.07354 5.07354 3.6146 5.63803 3.32698C6.27976 3 7.11984 3 8.8 3H15.2C16.8802 3 17.7202 3 18.362 3.32698C18.9265 3.6146 19.3854 4.07354 19.673 4.63803C20 5.27976 20 6.11984 20 7.8V21L17.25 19L14.75 21L12 19L9.25 21L6.75 19L4 21V7.8Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

interface TaxesFeesCardProps {
    inputs: PremiumSelectionInputs;
    derived: PremiumSelectionDerived | null;
    setInput: (key: InputKey, value: string) => void;
    commit: (key: InputKey) => void;
    // ── InsCipher surplus-lines calculation (OR-1440) ──────────────
    /** False until the submission reaches "Indications Sent" (the backend 409s
     *  before that), which disables the button and swaps its tooltip. */
    canCalculateTax: boolean;
    taxCalcPhase: TaxCalcPhase;
    /** Tooltip for the neutral "already up to date" mark. */
    taxCalcCurrentMessage?: string;
    /** Drives the provenance ✓ — see `InsCipherRow`. */
    isInsCipherAuthored: boolean;
    /** When InsCipher last ran; null until backend PR #327 ships the column. */
    taxCalculatedAt: string | null;
    /** Bumped on a successful calculation to flash the two rate inputs. */
    taxCalcFlashKey: number;
    onCalculateTax: () => void;
    // ── Custom (misc) fees (OR-1494) ────────────────────────────────
    /** Underwriter-defined fee lines persisted on the quote. */
    miscFees: MiscFee[];
    /** Create the free-text rows the modal saved — the page owns the POSTs.
     *  Awaited by the modal, which stays open (and shows the error) if it
     *  rejects; reject with `CustomFeeSaveError` to keep only the failed rows. */
    onAddCustomFees: (entries: { name: string; amount: number }[]) => Promise<void>;
    /** Delete one misc fee row from the card (no modal involved). */
    onDeleteMiscFee: (id: string) => void;
}

/** Documents print an unnamed misc fee under this label (backend OR-1421);
 *  the card mirrors it so the row is never blank. */
const UNNAMED_MISC_FEE_LABEL = "Miscellaneous Fee";

export function TaxesFeesCard({
    inputs,
    derived,
    setInput,
    commit,
    canCalculateTax,
    taxCalcPhase,
    taxCalcCurrentMessage,
    isInsCipherAuthored,
    taxCalculatedAt,
    taxCalcFlashKey,
    onCalculateTax,
    miscFees,
    onAddCustomFees,
    onDeleteMiscFee,
}: TaxesFeesCardProps) {
    const [addFeeOpen, setAddFeeOpen] = useState(false);
    const addedFeeIds = EXTRA_FEE_TYPES.filter((t) => inputs[t.id] !== "").map((t) => t.id);

    return (
        <FormGroup
            title="Taxes & Fees"
            icon={TaxesFeesIcon}
            data-autotest="premium-selection-taxes-fees-card"
            action={
                <Button color="link-gray" size="sm" onClick={() => setAddFeeOpen(true)} data-autotest="premium-selection-taxes-fees-add-fee-button">
                    + Add Fee
                </Button>
            }
        >
            <CardRows>
                <TechRmFeeRow value={inputs.techRmFee} setInput={setInput} commit={commit} />

                <PercentRow
                    inputKey="triaPct"
                    label="TRIA"
                    value={inputs.triaPct}
                    amount={derived?.tria ?? null}
                    setInput={setInput}
                    commit={commit}
                    dataAutotest="premium-selection-taxes-fees-tria-row"
                />

                {/* Added extra fees (OR-1339) — one row per predefined fee the
                  * underwriter has picked via the modal, in catalog order. They
                  * sit between TRIA and the InsCipher block per the design.
                  * Deleting here clears the input directly, no modal needed. */}
                {EXTRA_FEE_TYPES.filter((t) => inputs[t.id] !== "").map((t) => (
                    <ExtraFeeRow
                        key={t.id}
                        label={t.label}
                        value={inputs[t.id]}
                        onDelete={() => {
                            setInput(t.id, "");
                            commit(t.id);
                        }}
                    />
                ))}

                {/* Custom fees (OR-1494) — the misc fee lines saved on the quote,
                  * in creation order, after the predefined ones. Deleting one
                  * removes the row and the backend record right away. */}
                {miscFees.map((fee) => (
                    <ExtraFeeRow
                        key={fee.id}
                        label={fee.name || UNNAMED_MISC_FEE_LABEL}
                        value={fee.amount === null ? "" : String(fee.amount)}
                        onDelete={() => onDeleteMiscFee(fee.id)}
                    />
                ))}

                {/* The InsCipher block. Shaded as one group per the design; the
                  * rates stay editable because manual entry is still supported —
                  * typing one just drops the provenance ✓.
                  *
                  * Geometry per the Figma: the shading sits slightly WIDER than
                  * the card's rows (`-mx-3`) but stops short of the card edge,
                  * and its rows are indented one step further in (`[&>*]:px-3`).
                  * The dashed hairlines therefore line up with the card's own
                  * separators above while the labels sit inset — which is why
                  * the padding is on the rows rather than on the shaded box. */}
                <div className="bg-secondary -mx-3 mt-1 rounded-lg px-3 py-1 !border-t-0">
                    <div className="[&>*+*]:border-secondary flex flex-col [&>*]:px-3 [&>*+*]:border-t [&>*+*]:border-dashed">
                        <InsCipherRow
                            canCalculate={canCalculateTax}
                            phase={taxCalcPhase}
                            currentMessage={taxCalcCurrentMessage}
                            isInsCipherAuthored={isInsCipherAuthored}
                            calculatedAt={taxCalculatedAt}
                            onCalculate={onCalculateTax}
                            data-autotest="premium-selection-taxes-fees-inscipher-row"
                        />
                        <PercentRow
                            inputKey="surplusLinesTaxPct"
                            label="Surplus Lines Tax"
                            value={inputs.surplusLinesTaxPct}
                            amount={derived?.surplusLinesTax ?? null}
                            setInput={setInput}
                            commit={commit}
                            externalFlashKey={taxCalcFlashKey}
                            dataAutotest="premium-selection-taxes-fees-surplus-lines-tax-row"
                        />
                        <PercentRow
                            inputKey="surplusLinesFeePct"
                            label="Surplus Lines Fee"
                            value={inputs.surplusLinesFeePct}
                            amount={derived?.surplusLinesFee ?? null}
                            setInput={setInput}
                            commit={commit}
                            externalFlashKey={taxCalcFlashKey}
                            dataAutotest="premium-selection-taxes-fees-surplus-lines-fee-row"
                        />
                        <PercentRow
                            inputKey="stampingFeePct"
                            label="Stamping Fee"
                            value={inputs.stampingFeePct}
                            amount={derived?.stampingFee ?? null}
                            setInput={setInput}
                            commit={commit}
                            externalFlashKey={taxCalcFlashKey}
                            dataAutotest="premium-selection-taxes-fees-stamping-fee-row"
                        />
                    </div>
                </div>
            </CardRows>

            <AddFeeModal
                isOpen={addFeeOpen}
                onOpenChange={setAddFeeOpen}
                existingFeeIds={addedFeeIds}
                onSave={async ({ predefined, custom }) => {
                    for (const entry of predefined) {
                        setInput(entry.id, entry.amount);
                        commit(entry.id);
                    }
                    if (custom.length > 0) await onAddCustomFees(custom);
                }}
            />
        </FormGroup>
    );
}

/** Provisional copy (OR-1700): the design has no error state for this row
 *  yet. Pending design confirmation. */
const TECH_RM_FEE_ERROR = "Enter a multiple of $5,000.";

/**
 * Tech/RM Fee: a dollar input the backend derives until the underwriter types
 * over it (backend PR #371). Mirrors `PercentRow`'s geometry (label left, control
 * right, blur flash) with `MoneyInput` in place of the % field.
 *
 * Typing edits a local draft; nothing reaches the quote inputs (and so no
 * autosave) until blur accepts it (OR-1700):
 * - a multiple of $5,000, 0 included (removes the fee), is committed;
 * - any other amount stays as typed and flags the field, because the backend
 *   would silently round it ($500 → $0);
 * - an emptied field restores the last committed fee rather than sending
 *   `null`, which would quietly hand the fee back to the derivation.
 */
function TechRmFeeRow({
    value,
    setInput,
    commit,
}: {
    value: string;
    setInput: (key: InputKey, value: string) => void;
    commit: (key: InputKey) => void;
}) {
    const { flashKey, flash } = useBlurFlash();
    const [draft, setDraft] = useState(value);
    const [isInvalid, setIsInvalid] = useState(false);
    // Read on blur: the draft state may not have flushed yet on a fast
    // type-then-blur.
    const latestRaw = useRef(value);
    // A new committed fee (load, post-save re-GET) replaces whatever draft
    // is on screen.
    useEffect(() => {
        latestRaw.current = value;
        setDraft(value);
        setIsInvalid(false);
    }, [value]);
    return (
        <div data-autotest="premium-selection-taxes-fees-tech-rm-fee-row" className="flex min-h-15 items-center justify-between gap-4 py-3">
            <span className="text-secondary text-sm font-medium">Tech/RM Fee</span>
            <div className="flex flex-col items-end gap-1.5">
                <div key={flashKey} className={`w-40 shrink-0 rounded-lg${flashKey > 0 ? " field-flash" : ""}`}>
                    <MoneyInput
                        aria-label="Tech/RM Fee"
                        value={draft}
                        isInvalid={isInvalid}
                        onChange={(raw) => {
                            latestRaw.current = raw;
                            setDraft(raw);
                        }}
                        onBlur={() => {
                            const raw = latestRaw.current;
                            if (raw.trim() === "") {
                                latestRaw.current = value;
                                setDraft(value);
                                setIsInvalid(false);
                                return;
                            }
                            // Wire range first (Numeric(14,2)).
                            const clamped = clampMoneyToWire(raw);
                            if (!isValidTechRmFee(clamped)) {
                                setIsInvalid(true);
                                return;
                            }
                            setIsInvalid(false);
                            setInput("techRmFee", clamped);
                            commit("techRmFee");
                            flash();
                        }}
                    />
                </div>
                {isInvalid && (
                    <p className="text-error-primary text-sm" data-autotest="premium-selection-taxes-fees-tech-rm-fee-error">
                        {TECH_RM_FEE_ERROR}
                    </p>
                )}
            </div>
        </div>
    );
}

/** One added extra fee: label, its dollar amount, and a trash icon that
 *  deletes it in place (design: "Delete Fees Without Reopening the Modal"). */
function ExtraFeeRow({ label, value, onDelete }: { label: string; value: string; onDelete: () => void }) {
    return (
        <div className="flex min-h-15 items-center justify-between gap-4 py-3" data-autotest="premium-selection-taxes-fees-extra-fee-row">
            <span className="text-secondary text-sm font-medium">{label}</span>
            <div className="flex items-center gap-3">
                <span className="text-primary text-sm font-semibold tabular-nums">{displayMoneyValue(Number(value))}</span>
                <ButtonUtility
                    color="tertiary"
                    size="sm"
                    icon={Trash02}
                    tooltip="Delete fee"
                    onClick={onDelete}
                    data-autotest="premium-selection-taxes-fees-extra-fee-delete-button"
                />
            </div>
        </div>
    );
}

/** Editable whole-percent row (TRIA / Surplus Lines): the user sets the
 *  rate; the derived $ contribution shows read-only to its left (Andressa,
 *  jul-22 — "leave the % field, display the value in dollars"). No info
 *  tooltip on these rows per the design. */
function PercentRow({
    inputKey,
    label,
    value,
    amount,
    setInput,
    commit,
    externalFlashKey = 0,
    dataAutotest,
}: {
    inputKey: InputKey;
    label: string;
    value: string;
    /** $ contribution. Rate-derived — TRIA off selected × 5% × rate%,
     *  Surplus Lines off selected × rate% alone — except for the
     *  surplus-lines pair once InsCipher has calculated, where it's instead
     *  the amount InsCipher filed (OR-1440). */
    amount: number | null;
    setInput: (key: InputKey, value: string) => void;
    commit: (key: InputKey) => void;
    /** Bumped by the parent to flash this row without a blur — the design asks
     *  for the inputs to highlight for a second when a calculation lands. */
    externalFlashKey?: number;
    /** Custom attribute for E2E testing — applied to the row's outer `<div>`. */
    dataAutotest?: string;
}) {
    const { flashKey: blurFlashKey, flash } = useBlurFlash();
    // Either trigger restarts the CSS animation; summing keeps the key strictly
    // increasing so two flashes in a row still re-key.
    const flashKey = blurFlashKey + externalFlashKey;
    return (
        <div data-autotest={dataAutotest} className="flex min-h-15 items-center justify-between gap-4 py-3">
            <span className="text-secondary text-sm font-medium">{label}</span>
            <div className="flex items-center gap-3">
                <span className="text-tertiary text-sm tabular-nums">{displayMoneyValue(amount)}</span>
                <div key={flashKey} className={`w-24 shrink-0 rounded-lg${flashKey > 0 ? " field-flash" : ""}`}>
                    <PercentInput
                        aria-label={label}
                        value={value}
                        onChange={(raw) => setInput(inputKey, raw)}
                        onBlur={() => {
                            // Keep the PUT inside the wire's Numeric(8,6) range.
                            const clamped = clampPctToWire(value);
                            if (clamped !== value) setInput(inputKey, clamped);
                            commit(inputKey);
                            flash();
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
